import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-kit";
import { Pager, fmtTime } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck } from "lucide-react";
import { PushEnableCard, PushSettingsCard } from "@/components/PushCards";

const EVENT_LABEL = { student_absent: "Absent", fee_due: "Fee due", fee_payment_approved: "Payment approved", fee_payment_rejected: "Payment rejected",
  salary_credited: "Salary", leave_approved: "Leave", school_holiday: "Holiday", new_announcement: "Announcement", teacher_message: "Teacher message",
  parent_message: "Parent message", diary: "Diary", task: "Task", ack: "Seen", student_update: "Daily update" };

export default function NotificationsPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [unread, setUnread] = useState(false);
  const load = () => api.get("/notifications", { params: { page, unread } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [page, unread]); // eslint-disable-line react-hooks/exhaustive-deps

  async function open(n) {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.link) nav(n.link); else load();
  }
  async function readAll() { await api.post("/notifications/read-all"); load(); }

  return (
    <AppShell>
      <PageHeader eyebrow="Inbox" title="Notifications" subtitle="Diary, task and acknowledgment alerts.">
        <Button data-testid="notif-filter" variant="outline" onClick={() => { setUnread(!unread); setPage(1); }}>{unread ? "Show all" : "Unread only"}</Button>
        <Button data-testid="notif-read-all" onClick={readAll} className="btn-brand"><CheckCheck size={15} className="mr-1" /> Mark all read</Button>
      </PageHeader>
      <PushEnableCard />
      <PushSettingsCard />
      {data.items.length === 0 ? <Empty testid="notif-empty" icon={Bell} text="No notifications." /> : (
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => open(n)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-violet-50/50 transition-colors ${n.read ? "" : "bg-violet-50/60"}`}>
              <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-violet-600"}`} />
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-slate-900">{n.title}{EVENT_LABEL[n.kind] && <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">{EVENT_LABEL[n.kind]}</span>}</span>
                <span className="block text-sm text-slate-600">{n.body}</span>
                <span className="block text-xs text-slate-400 mt-0.5">{fmtTime(n.created_at)} • {n.read ? "Read" : "Unread"}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
    </AppShell>
  );
}
